import NumberHelper from "./NumberHelper";
import FileSizeUnit from "../units/FileSizeUnit";
import {Base64} from "js-base64";
import FunctionHelper from "./FunctionHelper";

export const PATH_SEPARATOR = "/";

/**
 * The FileHelper bundles useful static functions for file handling.
 */
export default class FileHelper {
	/**
	 * Returns a given size in bytes as formatted size string with {@link FileSizeUnit}. Automatically using the biggest
	 * possible {@link FileSizeUnit}.
	 */
	public static formatFileSize(size: number, decimals?: number): string {
		if (size < 1024) {
			return this.formatFileSizeAs(FileSizeUnit.BYTES, size, decimals) + ' ' + FileSizeUnit.BYTES;
		} else if (size < 1024 * 1024) {
			return this.formatFileSizeAs(FileSizeUnit.KILOBYTES, size, decimals) + ' ' + FileSizeUnit.KILOBYTES;
		} else if (size < 1024 * 1024 * 1024) {
			return this.formatFileSizeAs(FileSizeUnit.MEGABYTES, size, decimals) + ' ' + FileSizeUnit.MEGABYTES;
		} else if (size < 1024 * 1024 * 1024 * 1024) {
			return this.formatFileSizeAs(FileSizeUnit.GIGABYTES, size, decimals) + ' ' + FileSizeUnit.GIGABYTES;
		} else {
			return this.formatFileSizeAs(FileSizeUnit.TERRABYTES, size, decimals) + ' ' + FileSizeUnit.TERRABYTES;
		}
	}

	/**
	 * Returns a given size in bytes as formatted size by the given {@link FileSizeUnit}.
	 */
	public static formatFileSizeAs(unit: FileSizeUnit, size: number, decimals?: number): number {
		switch (unit) {
			case FileSizeUnit.KILOBYTES:
				return NumberHelper.format(size / (1024), decimals);
			case FileSizeUnit.MEGABYTES:
				return NumberHelper.format(size / (1024 * 1024), decimals);
			case FileSizeUnit.GIGABYTES:
				return NumberHelper.format(size / (1024 * 1024 * 1024), decimals);
			case FileSizeUnit.TERRABYTES:
				return NumberHelper.format(size / (1024 * 1024 * 1024 * 1024), decimals);
			default:
				return NumberHelper.format(size, decimals);
		}
	}

	/**
	 * Creates virtual link elements and clicks them to download the given files
	 */
	public static async download(files: Array<{ name: string, url: string }>): Promise<void> {
		for (let file of files.values()) {
			let root: HTMLElement = FunctionHelper.applyValue(document.body, document.documentElement);
			let link: HTMLAnchorElement = document.createElement('a');
			link.href = file.url;
			link.target = '_blank';
			link.download = file.name;
			root.appendChild(link);

			await new Promise((resolve) => {
				setTimeout(resolve, 500);
			});

			link.click();
			link.parentNode!.removeChild(link);
		}
	}

	/**
	 * Checks if a given string is a valid filename.
	 */
	public static isFilename(value: string): boolean {
		let regexp: RegExp = /^((?![\\/:*?" <>|]).|\s)+$/;
		return regexp.test(value);
	}

	/**
	 * Converts a given {@link ArrayBuffer} to a base64 string
	 */
	public static ArrayBufferToBase64(data: ArrayBuffer): string {
		let rawData: Array<number> = Array.from(new Uint8Array(data));
		let result: string = "";

		let parts: Array<number> = [];
		let byteLength: number = 0;
		for (let value of rawData) {
			parts.push(value);
			byteLength++;

			// limit parts to avoid too many parameters error
			if (byteLength >= 10000) {
				result += String.fromCharCode(...parts);
				byteLength = 0;
				parts = [];
			}
		}

		// add left over parts
		if (byteLength > 0) {
			result += String.fromCharCode(...parts);
		}

		// convert to base64
		return Base64.btoa(result);
	}
}